import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { groupBy } from 'lodash';

import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import { Field, reduxForm, SubmissionError } from 'redux-form';

import SwitchField from 'components/fields/SwitchField';

import { updateUserNotificationSettings } from 'redux/modules/userNotificationSettings';

import objectDiff from 'utils/objectDiff';

const styles = theme => ({
  form: {
    padding: '0 40px 24px',
    [theme.breakpoints.down('xs')]: {
      padding: '0 8px 8px',
    },
  },
  section: {
    paddingTop: 8,
    paddingBottom: 8,

    '& + $section': {
      borderTop: `1px solid ${theme.palette.primary.A100}`,    
    },
  },
  typographyDescription: {
    marginTop: 2,
  },
});

function onSubmit(values, dispatch, props) {
  const normalizeValues = (formValues) => {
    const normalizedFormValues = Object.assign({}, formValues);
    Object.keys(normalizedFormValues).forEach((key) => {
      normalizedFormValues[key] = values[key];
    })
    return normalizedFormValues;
  };
  const diffValues = objectDiff(values, props.initialValues);
  return new Promise((resolve, reject) => {
    dispatch(updateUserNotificationSettings({
      values: normalizeValues(diffValues),
      resolve,
      reject,
    }));
  }).then(res => res,
    (err) => {
      if (err.error) {
        throw new SubmissionError({ _error: err.error });
      }
      throw new SubmissionError(err);
    });

}

let syncingUpdates = false;
const syncTimeout = 100;

function onChange(values, dispatch, props, prevValues) {
  if (!syncingUpdates) {
    const diffValues = objectDiff(values, prevValues);
    syncingUpdates = true;
    Object.keys(diffValues).forEach((key) => {
      if (values[key]['Is All'] && !prevValues[key]['Is All']
        && (prevValues[key]['Is Assigned'] || prevValues[key]['Is Watcher'])) {
        dispatch(props.change(`${key}.Is Assigned`, false));
        dispatch(props.change(`${key}.Is Watcher`, false));          
      }
      if ((values[key]['Is Assigned'] && !prevValues[key]['Is Assigned']
        || values[key]['Is Watcher'] && !prevValues[key]['Is Watcher']) && values[key]['Is All']) {
        dispatch(props.change(`${key}.Is All`, false));
      }
    });

    setTimeout(() => {
      syncingUpdates = false;
    }, syncTimeout);
  }
}

const NotificationsSettingsDialogForm = ({ handleSubmit, userNotificationSettings, classes, initialValues, width }) => {
  let userNotificationSettingsBySubject = groupBy(userNotificationSettings, (item) => {
    if (item['Sub Subject'] === 'authorization') {
      return [item['Subject'], item['Event Type']];
    }
    if (item['Sub Subject'] === 'date_of_service') {
      return [item['Subject'], item['Event Type']];
    }
    return [item['Subject'], item['Sub Subject']].filter(item => item);
  });
  const subjectList = {
    patient: 'Patient',
    'patient,patient_sms': 'Patient SMS',
    'patient,patient_document': 'Patient Documents',
    'patient,patient_notes': 'Patient Notes',
    order: 'Order',
    'order,order_notes': 'Order Notes',
    'order,remind': 'Order Dates Expiration',
  };
  const getIndexInGroup = (comparator) => {
    switch (comparator) {
      case 'create':
        return -5;
        break;
      case 'update':
        return -4;
        break;
      case 'delete':
        return -3;
        break;
      case 'remind':
        return -2;
        break;
      case 'change_resource':
        return -1;
        break;
      default:
        return 1;
    }
  }
  const groupedUserNotificationSettings = Object.keys(userNotificationSettingsBySubject)
    .sort((keyA, keyB) => 
      Object.keys(subjectList).indexOf(keyA) > Object.keys(subjectList).indexOf(keyB) ? 1 : -1)
    .map(key => ({
      Subject: subjectList[key] || key,
      items: userNotificationSettingsBySubject[key].sort((itemA, itemB) =>
        getIndexInGroup(itemA['Event Type'])
          - getIndexInGroup(itemB['Event Type'])),
    }))
  return (
    <form
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit}
      className={classes.form}
    >
      {groupedUserNotificationSettings.map(({ Subject, items }) => (
        <div
          className={classes.section}
          key={Subject}
        >
          <Grid
            container
            alignItems="flex-start"
            spacing={16}
            wrap="nowrap"
          >
            {width !== 'xs' && (
              <Grid
                item
                xs={2}
              >
                <Typography
                  color="secondary"
                  variant="subheading"
                >
                  {Subject}
                </Typography>
              </Grid>
            )}
            <Grid
              item
              xs={12}
              sm={10}
            >
              <div>
                {items.map(item => (
                  <Grid
                    key={item.ID}
                    container
                    alignItems="flex-start"
                    spacing={16}
                    wrap="nowrap"
                  >
                      <Grid 
                        item
                        xs={6}
                      >
                        <Typography className={classes.typographyDescription}>
                          {item.Description}
                        </Typography>
                      </Grid>
                      <Grid 
                        item
                        xs={2}
                      >
                        <Field
                          id={`${item.ID}.Is All`}
                          name={`${item.ID}.Is All`}
                          component={SwitchField}
                        />
                      </Grid>
                      {([item['Subject'], item['Sub Subject'], item['Event Type']].filter(key => key).join(' ') !== 'patient create'
                        && item.ID !== 'patient_create') &&
                        <Fragment>
                          <Grid 
                            item
                            xs={2}
                          >
                            <Field
                              id={`${item.ID}.Is Assigned`}
                              name={`${item.ID}.Is Assigned`}
                              component={SwitchField}
                            />
                          </Grid>
                          <Grid 
                            item
                            xs={2}
                          >
                            <Field
                              id={`${item.ID}.Is Watcher`}
                              name={`${item.ID}.Is Watcher`}
                              component={SwitchField}
                            />
                          </Grid>
                        </Fragment>
                      }
                  </Grid>
                ))}   
              </div>
            </Grid>
          </Grid>
        </div>
      ))}
    </form>
  );
};

NotificationsSettingsDialogForm.propTypes = {
  userNotificationSettings: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  className: PropTypes.string,
  classes: PropTypes.shape({
    form: PropTypes.string.isRequired,
    section: PropTypes.string.isRequired,
  }).isRequired,
};

NotificationsSettingsDialogForm.defaultProps = {
  className: '',
};

export default reduxForm({
  form: 'notificationsSettingsForm',
  enableReinitialize: true,
  onSubmit,
  onChange,
})(withStyles(styles)(withWidth()(NotificationsSettingsDialogForm)));



// WEBPACK FOOTER //
// ./src/components/home/NotificationsSettingsDialog/NotificationsSettingsDialogForm.js